import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import pfx from "../../utils/style-class-prefix-helper";
import { HEADLINE_SIZE_H4, HEADLINE_TAG_H4 } from "../headline/constants/headline-constants";
import { cookieService } from "../../utils/cookie-helper";
import { ButtonRank } from "../button/constants/button-types";
import { BUTTON_SCHEME_MAP, BUTTONRANK_PRIMARY } from "../button/constants/button-constants";
import { footerColors, HeadlineColor, HeadlineSize } from "./constants/modal-types";
import { isMobile } from "../../utils/screen-helper";
import { BackgroundColor } from "../background/constants/background-types";
import { getNestedChildrenOf } from "../../utils/dom-helper";
import { HeadlineTag } from "../headline/constants/headline-types";
import { useTheRightHtmlTagForHeadline } from "../../utils/headline-helper";
import { MODAL_DEFAULT_HEADLINE_COLOR, MODAL_ICON_SCHEME } from "../../constants/global-constants";
import webComponentsDebug from "../../utils/log-helper";
import { KeyIds } from "../../constants/keys";
import { ConditionalWrapper } from "../../common/conditional-wrapper";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [button-right] - Holds a single eon-ui-button element. It is used for the right button
 * @slot [unnamed] - holds any eon-ui element or HTML element. Use this slot if the copytext is not enough
 */

@Component({
  tag: "eon-ui-modal",
  styleUrl: "modal.scss",
  shadow: true
})
export class Modal {
  // @InitLogging() sentryTransaction;
  @State() buttonItems: Element[];
  @State() processedText: string;
  @State() seoHeadlineTag: string;

  button: any;
  private modalWrapper: HTMLElement;
  hasButtonRightSlot: boolean;
  private modalElement: HTMLElement;
  private buttonContainerElement: HTMLElement;
  private closeButton: HTMLElement;
  private triggerElement: HTMLElement;

  /**
   * Raised when the modal has been opened.
   */
  @Event() modalOpen: EventEmitter;

  /**
   * Raised when the modal has been closed via button.
   */
  @Event() modalClose: EventEmitter;

  /**
   * Raised when the modal component did load.
   */
  @Event() modalLoad: EventEmitter;

  @Element() hostElement: HTMLEonUiModalElement;

  /**
   * if this property is true, the modal window will be shown
   */
  @Prop({ mutable: true, reflect: true }) visible: boolean = false;

  /**
   * the given ID for the modal.<br>It is important that you set the modalID if you use more them one modal window in one page.
   */
  @Prop({ reflect: true }) modalId: string = "modal";

  /**
   * if this property is true, a optional closing-X will be shown
   */
  @Prop() showClosingX: boolean;

  /**
   * the Timeout in milliseconds to open the modal automatically (1000ms -> 1 second)<br>
   *   set the timeout to 1 means that the modal windows opens immediately
   */
  @Prop() openTimeout: number;

  /**
   * if the the cookie-name-on-close is set, a cookie with this name will be written and on the next time the modal will not be automatically opened
   */
  @Prop() cookieNameOnClose: string;

  /**
   * set the lifetime of the cookie<br>it means how long is the cookie valid. lifetime in days, default 1 Day
   */
  @Prop() cookieExpirationDays: number = 1;

  /**
   * The text of the eon-ui-headline inside the Modal (see eon-ui-headline)
   */
  @Prop() headlineText: string;

  /**
   * The possibility to set a simple text inside the Modal<br>Use \n for linebreak like headline
   */
  @Prop() copytext: string;

  /**
   * set the text for the right close-button.<br>If no text is set, no close-button will be shown
   */
  @Prop() closeButtonText: string;

  /**
   * Use primary whenever the button is the main CTA in the current context.
   * Secondary buttons should only appear next to primary buttons and are rendered as "outline" button.
   */
  @Prop() closeButtonRank: ButtonRank = BUTTONRANK_PRIMARY;

  /**
   * if this property is true, the modal window will have a width almost over the entire width
   */
  @Prop() fullWidth: boolean = false;

  /**
   * set the width of the modal window if the full-Width is set, possible units are "%" or "px"<br>
   *   example: "500px" or "99%"<br>
   *   the value is only valid for desktop, it wil be overwritten for tablet by 95% and for phone by 100%
   */
  @Prop() fullWidthWidth: string;
  /**
   * set the max-width of the modal window if the full-Width is set, possible units are "%" or "px"<br>
   *   example: 1200px or 99%
   */
  @Prop() fullWidthMaxWidth: string;

  /**
   * set the-z-index, in some application it is necessary to increase the z-index, with these properties you can do it
   * Default: 300
   *
   */
  @Prop({ mutable: true }) zIndex: number = 300;

  /**
   * if thus property is selected, the footer background has the chosen color
   */
  @Prop({ reflect: true, mutable: true }) scheme: footerColors;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for headline.
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * The size of the headline.
   */
  @Prop() headlineSize: HeadlineSize = HEADLINE_SIZE_H4;

  /**
   * The color of the headline.
   */
  @Prop() headlineColor: HeadlineColor = MODAL_DEFAULT_HEADLINE_COLOR;

  /**
   * Moves the modal to end of document to avoid a problem with stacking context. If you set this to false, the modal will stay where it is defined. This is especially helpful when a stable reference is needed.
   */
  @Prop() moveModalToEndOfBody: boolean = true;

  componentWillLoad() {
    this.initModal();
    this.updateProcessedText();
    if (this.showClosingX) this.closeModalByClickOutsideModalContent();
    this.handleHeadlineTag();
  }

  componentDidLoad() {
    this.setHtmlElements();
    this.checkButton();
    this.modalLoad.emit();
  }

  private handleModalCloseClick = (closeType: "x" | "button" | "escape" | "outside") => {
    this.visible = false;
    this.modalClose.emit(closeType);
    const focusTarget = this.triggerElement?.hasAttribute("tabindex")
      ? this.triggerElement
      : (this.triggerElement?.shadowRoot?.querySelector("[tabindex]") as HTMLElement);
    focusTarget?.focus();
  };

  private setHtmlElements() {
    // sets HTML-Elements
    this.modalElement = this.modalWrapper.querySelector(`div.${pfx("modal")}`);
    this.buttonContainerElement = this.modalWrapper.querySelector(`eon-ui-background`);
  }

  private setModalStyles = () => {
    try {
      this.modalElement.classList.remove(`${pfx("height-100")}`);
    } catch (e) {
      if (webComponentsDebug) console.log("too fast");
    }

    if (!!this.scheme) {
      if (isMobile()) {
        this.buttonContainerElement.style.width = window.innerWidth + "px";
      } else {
        this.buttonContainerElement.style.width = window.getComputedStyle(this.modalElement).getPropertyValue("width");
      }
    }
  };

  private setModalFullWidth = () => {
    try {
      this.modalElement.removeAttribute("style");
    } catch (e) {
      if (webComponentsDebug) console.log("too fast");
    }

    this.fullWidthWidth = this.fullWidthWidth ? this.fullWidthWidth : "99%";
    this.fullWidthMaxWidth = this.fullWidthMaxWidth ? this.fullWidthMaxWidth : "1200px";
    this.modalElement.style.width = this.fullWidthWidth;
    this.modalElement.style.maxWidth = this.fullWidthMaxWidth;
  };

  private closeModalByClickOutsideModalContent: () => void = () => {
    /*
     Why doesn't help event.target (event.srcElement is deprecated) like date-picker.tsx (line 279)?
     event.target stop at custom element EON_UI-MODAL. I get no information about the Tag, eg. div inside the shadow dom.
     only slotted Element could be reached but this ist not enough. you need the information if the wrapper-div is clicked.
     With event.composedPath() you get a List of HTMl-Nodes inside the clicked div, in this case the wrapper-div.
    */
    let modalThis = this;
    this.hostElement.addEventListener(
      //document.addEventListener(
      "click",
      function handler(event) {
        const path = event.composedPath();
        // Fetch reference to the element that was actually clicked
        const targetElement = path[0];
        if (targetElement === modalThis.modalWrapper) {
          modalThis.handleModalCloseClick("outside");
          window.removeEventListener("click", handler, false);
          event.stopPropagation();
        }
      },
      //In the evergreen browsers (everything except IE11) addEventListener has an additional option once
      // that automatically removes the Event Listener once the event has occurred.
      { once: false }
    );
  };

  /**
   * check and if necessary add styles for button-container
   * if the slot "button-right" and no Text for the close Button is filled out, hide the complete div "button-wrapper"
   */
  private checkButton: () => void = () => {
    this.hasButtonRightSlot = !!this.hostElement.querySelector('[slot="button-right"]');

    if (!this.hasButtonRightSlot && !this.closeButtonText) {
      let buttonContainer = this.modalWrapper.querySelector(`div.${pfx("button-container")}`);

      buttonContainer.classList.add(pfx("hidden").toString());
      this.modalElement.classList.add(pfx("has-no-button").toString());
    }

    if (!this.hasButtonRightSlot && !!this.closeButtonText) {
      this.modalElement.classList.add(pfx("has-one-button").toString());
    }
  };

  private delegateFooterColor: () => void = () => {
    this.innerBackground = this.scheme;
    const backgroundColor = `${this.scheme}`;

    //get slotted Button
    this.buttonItems = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-button"));

    //set scheme for slotted Button
    this.button = this.buttonItems;
    this.button.forEach((slotButton) =>
      slotButton.setAttribute("scheme", BUTTON_SCHEME_MAP[backgroundColor as BackgroundColor])
    );
  };

  private initModal: () => void = () => {
    this.visible = this.visible ? this.visible : false;
    this.zIndex = this.zIndex ? this.zIndex : 300;

    if (this.moveModalToEndOfBody) {
      let bodyElement = document.body;
      bodyElement.append(this.hostElement);
    }

    if (!!this.scheme) {
      this.delegateFooterColor();
    }

    const getCookieValueOnName = cookieService.getCookie(this.cookieNameOnClose);
    const autoTimeout = this.openTimeout ? this.openTimeout : 50;

    // check if the modal must be automatically opened
    if (!!this.openTimeout && !this.cookieNameOnClose) {
      setTimeout(() => {
        this.visible = true;
      }, this.openTimeout);
    }

    if (this.cookieNameOnClose) {
      // cookie configuration exists but no Cookie
      if (!getCookieValueOnName) {
        setTimeout(() => {
          this.visible = true;
        }, autoTimeout);
      }
    }
  };

  private handleKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case KeyIds.ESCAPE:
        if (this.showClosingX && this.visible) {
          this.handleModalCloseClick("escape");
          e.preventDefault();
          break;
        }
    }
  }

  @Watch("visible")
  reInitModal(): void {
    if (this.visible) {
      if (webComponentsDebug) console.log(`modal window: ${this.modalId} opened`);

      this.modalOpen.emit();

      if (!!this.scheme) {
        this.delegateFooterColor();
      }

      if (this.fullWidth) this.setModalFullWidth();
      this.onWindowResize();

      document.documentElement.style.overflow = "hidden";
      document.documentElement.style.height = "auto";
      // @ts-ignore
      document.body.scroll = "no";
      this.triggerElement = document.activeElement as HTMLElement;

      window.setTimeout(
        () => (this.hostElement.shadowRoot.querySelector(`.${pfx("modal")}`) as HTMLElement).focus(),
        10
      );
    } else {
      (this.hostElement.shadowRoot.querySelector(`.${pfx("modal")}`) as HTMLElement).blur();

      if (this.cookieNameOnClose) {
        cookieService.setCookie(this.cookieNameOnClose, "was-dismissed", this.cookieExpirationDays, false);
      }

      if (webComponentsDebug) console.log(`modal window: ${this.modalId} closed`);
      document.documentElement.style.overflow = "unset";
      document.documentElement.style.height = "100%";
    }
  }

  /**
   * Executes whenever the copytext property changes. Replaced the specified linewrap symbol with <br />.
   */
  @Watch("copytext")
  updateProcessedText() {
    const slashN = "\\n";

    if (this.copytext) {
      this.processedText = this.copytext.split(slashN).join("<br />");
    } else {
      this.processedText = this.copytext;
    }
  }

  @Watch("scheme")
  // This method is responsible to keep slotted components in sync with modal scheme
  // when it changes during the life of the component. This mostly applies to storybook controls in
  // canvas tab where changes to props don't trigger component reloading (In docs tab it does).
  passSchemeToChildren() {
    this.innerBackground = this.scheme;

    if (!!this.scheme) {
      const backgroundColor = `${this.scheme}`;
      this.closeButton.setAttribute("scheme", BUTTON_SCHEME_MAP[backgroundColor as BackgroundColor]);
    }
  }

  @Watch("headlineHtmlTag")
  handleHeadlineTag() {
    this.seoHeadlineTag = useTheRightHtmlTagForHeadline(this.headlineHtmlTag, HEADLINE_TAG_H4);
  }

  @Listen("resize", { target: "window" })
  onWindowResize() {
    setTimeout(() => {
      this.setModalStyles();
    }, 200);
  }

  render() {
    return (
      <Host role="alertdialog" aria-modal="true" aria-live="assertive">
        <div
          class={pfx({
            ["wrapper"]: true,
            ["visible"]: !!this.visible,
            ["full-width"]: !!this.fullWidth,
            ["has-footer-color"]: !!this.scheme
          })}
          style={{ zIndex: `${this.zIndex}` }}
          ref={(el) => (this.modalWrapper = el as HTMLElement)}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-static-element-interactions */}
          <div class={pfx("modal")} tabindex="0" onKeyDown={(event: KeyboardEvent) => this.handleKeyDown(event)}>
            {this.showClosingX && (
              <div class={pfx("closingX")}>
                <eon-ui-icon
                  class={pfx("icon-closing-x")}
                  onClick={() => this.handleModalCloseClick("x")}
                  name="close"
                  aria-label="close"
                  scheme={MODAL_ICON_SCHEME}
                ></eon-ui-icon>
              </div>
            )}
            <div class={pfx("content-wrapper")}>
              {this.headlineText && (
                <div class={pfx("headline-wrapper")}>
                  <eon-ui-headline
                    size={this.headlineSize}
                    text={this.headlineText}
                    headline-html-tag={this.seoHeadlineTag}
                    scheme={this.headlineColor}
                  ></eon-ui-headline>
                </div>
              )}
              {/*<div class={pfx("inner-content-wrapper")}>*/}
              <div class={pfx("scroll-container")}>
                <div class={pfx("content")} innerHTML={this.processedText}></div>
                <div class={pfx("content")}>
                  <slot />
                </div>
              </div>
              <ConditionalWrapper
                condition={!!this.scheme}
                wrapper={(children) => <eon-ui-background color={this.scheme}>{children}</eon-ui-background>}
                defaultWrapper={(children) => children}
              >
                <div
                  class={pfx({
                    ["button-container"]: true,
                    ["hide-closing-x"]: !this.showClosingX
                  })}
                >
                  {this.closeButtonText && (
                    <div class={pfx("button-left")}>
                      <eon-ui-button
                        full-width-on-mobile={true}
                        class={pfx("close-modal")}
                        rank={this.closeButtonRank}
                        text={this.closeButtonText}
                        ref={(ref) => {
                          this.closeButton = ref as HTMLElement;
                        }}
                        onClick={() => this.handleModalCloseClick("button")}
                      ></eon-ui-button>
                    </div>
                  )}
                  <div class={pfx("button-right")}>
                    <slot name="button-right" />
                  </div>
                </div>
              </ConditionalWrapper>
            </div>
            {/*</div>*/}
          </div>
        </div>
      </Host>
    );
  }
}
